import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Reveal from "@/components/Reveal";
import { ChevronDown } from "lucide-react";
import { scrollToId } from "@/App";

const FAQS = [
  {
    q: "What exactly does SORTD do?",
    a: "We're a hands-on business growth and commercial development partner. We connect you with the right people, run prospecting and follow-ups, train your sales team, manage your marketing and audit your business for growth opportunities — across New Zealand and Australia.",
  },
  {
    q: "Who do you typically work with?",
    a: "SME owners, real estate agencies, sales teams and corporate growth executives. If you need more conversations, more pipeline and more closed deals without hiring more staff, that's exactly what we do.",
  },
  {
    q: "How is this different from hiring an agency or a consultant?",
    a: "Consultants hand you a report. Agencies run ads. We do the actual work — real people making real calls, building real relationships and following up until opportunities convert. Strategy and execution in one place.",
  },
  {
    q: "Do you work with businesses outside New Zealand?",
    a: "Yes — we operate across both New Zealand and Australia, with dedicated regional specialists in each market.",
  },
  {
    q: "How do we get started?",
    a: "Book a strategy call through the form below. We'll learn where your business is at, identify the quickest wins, and come back with a practical plan — not a pitch deck.",
  },
];

export default function Faq() {
  const [openIdx, setOpenIdx] = useState(0);

  return (
    <section id="faq" data-testid="faq-section" className="py-20 sm:py-28">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <Reveal>
            <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 leading-tight">
              Frequently asked questions
            </h2>
          </Reveal>
          <Reveal>
            <p className="mt-4 text-base sm:text-lg text-slate-600">
              Everything you need to know about working with SORTD.
            </p>
          </Reveal>
        </div>

        <Reveal>
          <div data-testid="faq-accordion" className="divide-y divide-slate-200 border-y border-slate-200">
            {FAQS.map((f, i) => (
              <div key={i}>
                <button
                  data-testid={`faq-question-${i}`}
                  onClick={() => setOpenIdx(openIdx === i ? -1 : i)}
                  className="w-full flex items-center justify-between gap-4 py-5 text-left group"
                >
                  <span className="font-display text-base sm:text-lg font-semibold text-slate-900 group-hover:text-emerald-700 transition-colors">
                    {f.q}
                  </span>
                  <ChevronDown
                    className={`w-5 h-5 text-slate-400 shrink-0 transition-transform duration-300 ${
                      openIdx === i ? "rotate-180 text-emerald-600" : ""
                    }`}
                  />
                </button>
                <AnimatePresence initial={false}>
                  {openIdx === i && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                      className="overflow-hidden"
                    >
                      <p data-testid={`faq-answer-${i}`} className="pb-6 text-sm sm:text-base text-slate-600 leading-relaxed">
                        {f.a}
                      </p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </div>
        </Reveal>

        <Reveal className="text-center mt-8">
          <p className="text-sm text-slate-500">
            Can't find your answer?{" "}
            <button
              data-testid="faq-contact-link"
              onClick={() => scrollToId("contact")}
              className="font-semibold text-emerald-600 hover:text-emerald-700 transition-colors"
            >
              Ask us directly
            </button>
          </p>
        </Reveal>
      </div>
    </section>
  );
}
